import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';

export interface PersonLike {
	salutation?: string;
	title?: string;
	givenName?: string;
	familyName?: string;
	given?: string;
	family?: string;
	fullName?: string;
	additional?: string;
}

interface NameOptions {
	showSalutation: boolean;
	showTitle: boolean;
	showGivenName: boolean;
	showAdditional: boolean;
	salutationAsGivenNameFallback?: boolean;
}

const FALLBACK = '-';
const COMPANY = 'COMPANY';
const HIDDEN_SALUTATIONS = new Set(['DIV', 'UNKNOWN']);

@Injectable({ providedIn: 'root' })
export class PersonDisplayUtil {
	private readonly translocoService = inject(TranslocoService);

	getShortName(person: PersonLike | null | undefined): string {
		return this.buildName(person, {
			showSalutation: true,
			showTitle: false,
			showGivenName: false,
			showAdditional: false,
		});
	}

	getShortNameForOverview(person: PersonLike | null | undefined): string {
		return this.buildName(person, {
			showSalutation: false,
			showTitle: false,
			showGivenName: true,
			showAdditional: false,
			salutationAsGivenNameFallback: true,
		});
	}

	getLongName(person: PersonLike | null | undefined): string {
		return this.buildName(person, {
			showSalutation: true,
			showTitle: true,
			showGivenName: true,
			showAdditional: true,
		});
	}

	/**
	 * Zerlegt einen kombinierten `fullName` in Vor-/Nachname. EWS-Altbestände ohne getrennte
	 * Namensteile folgen der Konvention „Nachname Vorname" → erstes Token = Nachname, Rest =
	 * Vorname. Leerer Name → beide leer. Bereits getrennte `givenName`/`familyName` sind dem
	 * Ergebnis dieser Methode immer vorzuziehen (Aufrufer prüfen das vorab).
	 */
	splitFullName(fullName: string | null | undefined): { givenName: string; familyName: string } {
		const parts = (fullName ?? '').trim().split(/\s+/).filter(Boolean);
		if (parts.length === 0) {
			return { givenName: '', familyName: '' };
		}
		return { givenName: parts.slice(1).join(' '), familyName: parts[0] };
	}

	private buildName(person: PersonLike | null | undefined, options: NameOptions): string {
		if (!person) {
			return FALLBACK;
		}

		const fields = this.extractFields(person);
		const parts: string[] = [];

		if (options.showSalutation) {
			this.addSalutationPrefix(parts, fields.salutation);
		}

		if (fields.isCompany) {
			this.addCompanyParts(parts, fields, options);
		} else {
			this.addPersonParts(parts, fields, options);
		}

		return this.joinParts(parts);
	}

	private extractFields(person: PersonLike) {
		return {
			salutation: (person.salutation ?? '').trim(),
			isCompany: (person.salutation ?? '').trim() === COMPANY,
			givenName: (person.givenName ?? person.given ?? '').trim(),
			familyName: (person.familyName ?? person.family ?? '').trim(),
			fullName: (person.fullName ?? '').trim(),
			title: (person.title ?? '').trim(),
			additional: (person.additional ?? '').trim(),
		};
	}

	private addSalutationPrefix(parts: string[], salutation: string): void {
		const translated = this.visibleSalutation(salutation);
		if (translated) {
			parts.push(translated);
		}
	}

	private addCompanyParts(parts: string[], fields: ReturnType<PersonDisplayUtil['extractFields']>, options: NameOptions): void {
		parts.push(fields.fullName || FALLBACK);
		if (options.showAdditional && fields.additional) {
			parts.push(`(${fields.additional})`);
		}
	}

	private addPersonParts(parts: string[], fields: ReturnType<PersonDisplayUtil['extractFields']>, options: NameOptions): void {
		if (options.showTitle && fields.title) {
			parts.push(fields.title);
		}

		const salutationFallback = options.salutationAsGivenNameFallback ? this.visibleSalutation(fields.salutation) : '';
		const namePart = this.resolvePersonName(fields, options.showGivenName, salutationFallback);
		parts.push(namePart);
	}

	private resolvePersonName(fields: ReturnType<PersonDisplayUtil['extractFields']>, showGivenName: boolean, salutationFallback = ''): string {
		if (fields.familyName) {
			if (showGivenName) {
				const prefix = fields.givenName || salutationFallback;
				return prefix ? `${prefix} ${fields.familyName}` : fields.familyName;
			}
			return fields.familyName;
		}
		return fields.givenName || fields.fullName || FALLBACK;
	}

	private joinParts(parts: string[]): string {
		return (
			parts
				.map((p) => p.trim())
				.filter((p) => p.length > 0)
				.join(' ') || FALLBACK
		);
	}

	/**
	 * Übersetzte Anrede für die Anzeige — „technische" Anreden (DIV, UNKNOWN) und leere Werte
	 * werden unterdrückt. Gilt sowohl für den Anrede-Präfix als auch für den Vorname-Ersatz, damit
	 * z.B. eine Person ohne Vorname mit Anrede UNKNOWN nicht als „Unbekannt Nachname" erscheint.
	 */
	private visibleSalutation(salutation: string): string {
		if (!salutation || HIDDEN_SALUTATIONS.has(salutation)) {
			return '';
		}
		return this.translateSalutation(salutation);
	}

	private translateSalutation(salutation: string): string {
		const key = `workStepAttributeTypes.salutations.${salutation}`;
		const translated = this.translocoService.translate(key);
		return translated === key ? '' : translated;
	}
}
